import React from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { CheckCircle, XCircle, AlertTriangle, FileText, FileSearch, CircleCheckBig, Database, Calendar, Building, Hash, TrendingUp, X, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import TplSelect from '../Common/TplSelect';
import { vatDetectionStatus } from '../../../utils/vatDetectionState';

const ACCOUNTING_METHOD_OPTIONS = [
    { value: 'accrual', label: 'Faktureringsmetoden' },
    { value: 'cash', label: 'Kontantmetoden' },
];
const VAT_PERIOD_OPTIONS = [
    { value: 'monthly', label: 'Månadsvis' },
    { value: 'quarterly', label: 'Kvartalsvis' },
    { value: 'yearly', label: 'Årsvis' },
];

// SIE #GEN is YYYYMMDD; show it as an ISO date like every other date here.
const formatSieDate = (value) => {
    const raw = String(value || '');
    return /^\d{8}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : raw;
};

/**
 * A section the user can fold away. Used for the file's errors and warnings:
 * the verdict strip at the top already says whether the file is valid, so the
 * section only exists when there is something to read, and it opens with the
 * problems visible because the "importera trots fel" box asks the user to
 * have read them.
 */
function FoldedSection({ id, icon, title, detail, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const Chevron = open ? ChevronDown : ChevronRight;
    return (
        <div className="info-section">
            <button
                type="button"
                className="section-title fold-toggle"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={id}
            >
                {icon}
                <span>{title}</span>
                {detail && <span className="fold-detail">({detail})</span>}
                <span className="fold-action">{open ? 'Dölj' : 'Visa'}<Chevron size={14} aria-hidden="true" /></span>
            </button>
            {open && <div id={id} style={{ marginTop: '0.75rem' }}>{children}</div>}
        </div>
    );
}

const SieVerificationModal = ({
    show,
    onHide,
    verificationData,
    onConfirmImport,
    onCancel,
    isImporting,
    // Set by the parent after a successful import — switches the modal to its
    // confirmation instead of closing on a toast. "Klart" calls onCancel; in
    // onboarding that also carries the guide on.
    importResult,
    // A refused import (422 from the gate) — shown inline above the actions.
    importError,
}) => {

    const [fiscalYearSettings, setFiscalYearSettings] = useState({});
    // "Importera trots fel" — the user's explicit acknowledgement that the
    // file has validation errors. Sent to the backend, which refuses
    // error-carrying imports without it.
    const [acceptErrors, setAcceptErrors] = useState(false);
    useEffect(() => {
        setAcceptErrors(false);
    }, [verificationData]);

    useEffect(() => {
        if (preview.fiscalYears && preview.fiscalYears.length > 0) {
            const latestYear = preview.fiscalYears[0];
            const validVatPeriods = ['monthly', 'quarterly', 'yearly'];
            const validAccountingMethods = ['accrual', 'cash'];

            const detectedMethod = characteristics.accountingMethod.method;
            const detectedVatPeriod = characteristics.vatPeriod.period;

            setFiscalYearSettings({
                [latestYear.year]: {
                    accountingMethod: validAccountingMethods.includes(detectedMethod) ? detectedMethod : '',
                    vatPeriod: validVatPeriods.includes(detectedVatPeriod) ? detectedVatPeriod : '',
                    isOpen: characteristics.fiscalYearStatus.status !== 'closed'
                }
            });
        }
    }, [verificationData]);

    const characteristics = verificationData?.characteristics || {
        accountingMethod: { method: 'unknown', confidence: 0, indicators: [] },
        vatPeriod: { period: 'unknown', confidence: 0, indicators: [] },
        fiscalYearStatus: { status: 'unknown', confidence: 0, indicators: [] }
    };


    // Safe access with fallbacks
    const validation = verificationData?.verification || {
        isValid: false,
        errors: [],
        warnings: [],
        summary: {
            totalErrors: 0,
            totalWarnings: 0,
            verificationsChecked: 0,
            accountsChecked: 0
        }
    };

    const checks = verificationData?.checks || [];

    const preview = verificationData?.preview || {
        metadata: {},
        accountCount: 0,
        verificationCount: 0,
        transactionCount: 0,
        accountsWithTransactions: 0,
        fiscalYears: [],
        dateRange: {
            earliest: '',
            latest: ''
        },
        balanceSummary: {
            openingBalances: 0
        }
    };

    // Handle real metadata with safe access
    const metadata = preview?.metadata || {};
    const companyName = metadata.companyName || metadata.FNAMN || 'Okänt företag';
    const organizationNumber = metadata.organizationNumber || metadata.ORGNR || '';
    const programInfo = metadata.programInfo || metadata.PROGRAM || '';
    const generatedDate = formatSieDate(metadata.generatedDate || metadata.GEN || '');

    const handleConfirm = () => {
        if (onConfirmImport) {
            onConfirmImport(fiscalYearSettings, { acceptErrors });
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    // Check triage: errors block until explicitly accepted; warnings inform.
    const failedChecks = checks.filter(c => c.status === 'fail');
    const warnChecks = checks.filter(c => c.status === 'warn');
    const passedChecks = checks.filter(c => c.status === 'pass');
    const errorItemCount = failedChecks.reduce(
        (n, c) => n + (c.items?.filter(i => i.severity === 'error').length || 0), 0);
    const hasErrors = failedChecks.length > 0;

    // Check that all required fiscal year settings have been selected
    const latestFiscalYear = preview?.fiscalYears?.[0];
    const currentSettings = latestFiscalYear ? fiscalYearSettings[latestFiscalYear.year] : null;
    const settingsComplete = currentSettings
        && currentSettings.accountingMethod !== ''
        && currentSettings.vatPeriod !== '';
    const canImport = settingsComplete && (!hasErrors || acceptErrors);

    const formatNumber = (num) => {
        return new Intl.NumberFormat('sv-SE').format(num || 0);
    };

    // What the file is, for the header — the program that made it, the file's
    // name when the host kept a reference to it, and when it was exported (a
    // file exported before the last entries in the old program is incomplete;
    // the date is the only clue the user has).
    const sourceFileName = verificationData?.originalFile?.name || verificationData?.fileName || '';
    const sourceLine = [
        programInfo ? `Från ${programInfo}` : null,
        sourceFileName || null,
        generatedDate ? `exporterad ${generatedDate}` : null,
    ].filter(Boolean).join(' · ');

    const yearIsOpen = Boolean(currentSettings?.isOpen);

    // After the import: one calm heads-up, only when the momskontroll ran and
    // found accounts without a momsrapportkod. An unavailable or malformed
    // check says nothing here (review R9) — the momsdeklaration re-runs it and
    // asks there, which is where the decision belongs (owner, 2026-09-05).
    const vatGapCount = importResult && vatDetectionStatus(importResult.unmappedAccounts) === 'action_required'
        ? importResult.unmappedAccounts.accounts.length
        : 0;


    // Add helper functions after formatNumber function
    const handleFiscalYearSettingChange = (year, setting, value) => {
        setFiscalYearSettings(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [setting]: value
            }
        }));
    };

    // Chip styling for the detection confidence. App palette (style.css
    // tokens), not traffic-light hexes: high = brand-green tint, lower =
    // neutral gray. A red badge made a harmless, editable prefill look
    // like an alarm.
    const getConfidenceStyle = (confidence) => {
        if (confidence >= 80) {
            return { background: 'var(--rgba-primary-1)', color: 'var(--primary)' };
        }
        return { background: '#f1f5f9', color: 'var(--secondary, #717579)' };
    };
    const getConfidenceText = (confidence) => {
        if (confidence >= 80) return `${confidence}% säker`;
        if (confidence >= 60) return `${confidence}% – trolig`;
        return `${confidence}% – osäker`;
    };

    const getMethodLabel = (method) => {
        switch (method) {
            case 'accrual': return 'Faktureringsmetoden';
            case 'cash': return 'Kontantmetoden';
            default: return 'Okänd';
        }
    };

    const getVatPeriodLabel = (period) => {
        switch (period) {
            case 'monthly': return 'Månadsvis';
            case 'quarterly': return 'Kvartalsvis';
            case 'yearly': return 'Årsvis';
            default: return 'Okänd';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'open': return 'Öppet';
            case 'closed': return 'Stängt';
            default: return 'Okänt';
        }
    };

    // Wide while the file is being checked; the confirmation afterwards is
    // three lines and gets the default width.
    return (
        <Modal show={show} onHide={onHide} size={importResult ? undefined : 'lg'} backdrop="static" centered>
            <style>{`
                .sie-modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }
                
                .sie-modal-header {
                    background: #f8fafc;
                    color: #1e293b;
                    padding: 1.5rem 2rem;
                    border-bottom: 1px solid #e2e8f0;
                    position: relative;
                }
                
                .sie-modal-body {
                    padding: 2rem;
                    background: #ffffff;
                }
                
                .validation-summary {
                    background: #f1f5f9;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 2rem;
                    border-left: 4px solid ${validation.isValid ? '#10b981' : '#ef4444'};
                }
                
                .validation-summary.valid {
                    background: #f0fdf4;
                    border-left-color: #10b981;
                }
                
                .validation-summary.invalid {
                    background: #fef2f2;
                    border-left-color: #ef4444;
                }
                
                .validation-text {
                    margin: 0;
                    font-size: 0.875rem;
                    color: #374151;
                    font-weight: 500;
                }
                
                .metric-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }
                
                .metric-card {
                    background: white;
                    border-radius: 8px;
                    padding: 1.25rem;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                
                .metric-card:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }
                
                .metric-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 0.75rem;
                    background: #f1f5f9;
                    color: #64748b;
                }
                
                .metric-value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }
                
                .metric-label {
                    font-size: 0.75rem;
                    color: #64748b;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .info-section {
                    background: white;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .section-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .table-container {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 1.5rem;
                }
                
                .fiscal-years-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.875rem;
                }
                
                .fiscal-years-table thead {
                    background: #f8fafc;
                }
                
                .fiscal-years-table th {
                    padding: 0.75rem 1rem;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 1px solid #e2e8f0;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .fiscal-years-table td {
                    padding: 0.75rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                    color: #374151;
                }
                
                .fiscal-years-table tbody tr:hover {
                    background-color: #f8fafc;
                }
                
                .fiscal-years-table tbody tr:last-child td {
                    border-bottom: none;
                }
                
                .year-cell {
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .period-cell {
                    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                    font-size: 0.8rem;
                    color: #6b7280;
                }
                
                .company-info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1rem;
                }
                
                .company-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    transition: all 0.2s ease;
                }
                
                .company-card:hover {
                    border-color: #cbd5e1;
                    background: #f1f5f9;
                }
                
                .company-card.primary {
                    background: var(--rgba-primary-1);
                    border-color: var(--rgba-primary-3);
                }
                
                .company-card-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 6px;
                    background: #e2e8f0;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .company-card.primary .company-card-icon {
                    background: var(--primary);
                    color: white;
                }
                
                .company-card-content {
                    flex: 1;
                    min-width: 0;
                }
                
                .company-card-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    margin-bottom: 0.25rem;
                }
                
                .company-card-value {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    line-height: 1.4;
                    word-break: break-word;
                }
                
                
                
                
                
                
                .warning-item {
                    background: #fef3c7;
                    border: 1px solid #f59e0b;
                    border-radius: 6px;
                    padding: 1rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: start;
                    gap: 0.75rem;
                }
                
                .warning-icon {
                    color: #d97706;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }
                
                .warning-content {
                    flex: 1;
                }
                
                .warning-type {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #92400e;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    margin-bottom: 0.25rem;
                }
                
                .warning-message {
                    font-size: 0.875rem;
                    color: #92400e;
                    margin: 0;
                }

                .warning-item.warning-error {
                    background: #fee2e2;
                    border-color: #ef4444;
                }

                .warning-item.warning-error .warning-type {
                    color: #991b1b;
                }

                .warning-item.warning-error .warning-message {
                    color: #991b1b;
                }

                .error-icon {
                    color: #dc2626;
                }
                
                .no-data-message {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1.5rem;
                    background: #fef3c7;
                    border-radius: 6px;
                    color: #92400e;
                    font-size: 0.875rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #f59e0b;
                    font-weight: 500;
                }
                
                .modal-actions {
                    background: #f8fafc;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .action-button {
                    border-radius: 6px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .btn-cancel {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .btn-cancel:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .btn-import {
                    background: var(--primary);
                    color: white;
                }

                .btn-import:hover {
                    background: var(--primary-hover);
                }
                
                .btn-import:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .close-button {
                    position: absolute;
                    top: 1rem;
                    right: 1.5rem;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }
                
                .close-button:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                /* Responsive design */
                @media (max-width: 768px) {
                    .company-info-grid {
                        grid-template-columns: 1fr;
                    }
                    
                }

                .characteristics-section {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }
                .characteristics-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .characteristic-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    text-align: center;
                    transition: all 0.2s ease;
                }

                .characteristic-card:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .characteristic-header {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .characteristic-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #374151;
                    margin: 0 0 0.5rem 0;
                    text-align: center;
                }

                .confidence-badge {
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .characteristic-value {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .characteristics-grid {
                        grid-template-columns: 1fr;
                    }
    
                    .company-info-grid {
                        grid-template-columns: 1fr;
                    }
    
                }

                .characteristic-indicators {
                    font-size: 0.75rem;
                    color: #6b7280;
                    line-height: 1.5;
                    margin: 0.5rem 0 0;
                    padding-left: 1rem;
                }
                .characteristic-indicators li { margin-bottom: 2px; }

                .fiscal-year-config {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.25rem;
                }

                .settings-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }
                .setting-group-wide { grid-column: 1 / -1; }
                .setting-unset { font-size: 0.75rem; color: #92400e; }

                /* Räkenskapsårets status: two cards, one chosen. */
                .status-choice {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.75rem;
                }
                .status-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.625rem;
                    margin: 0;
                    padding: 0.75rem 0.875rem;
                    border: 1.5px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                    cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                }
                .status-card:hover { border-color: #cbd5e1; }
                .status-card.active {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }
                .status-card input[type="radio"] {
                    margin: 0.2rem 0 0;
                    flex-shrink: 0;
                    accent-color: var(--primary);
                }
                .status-card-text { display: flex; flex-direction: column; gap: 0.125rem; }
                .status-card-title { font-size: 0.875rem; font-weight: 600; color: #374151; }
                .status-card-desc { font-size: 0.75rem; color: #64748b; line-height: 1.4; }
                @media (max-width: 640px) {
                    .settings-grid, .status-choice { grid-template-columns: 1fr; }
                }

                .metric-value-text { font-size: 0.9375rem; line-height: 1.3; padding-top: 0.2rem; }
                .metric-value-text .nowrap { white-space: nowrap; }

                .fold-toggle {
                    width: 100%;
                    background: none;
                    border: 0;
                    padding: 0;
                    cursor: pointer;
                    text-align: left;
                }
                .fold-detail { font-weight: 400; color: #64748b; margin-left: 0.25rem; }
                .fold-action {
                    margin-left: auto;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.125rem;
                    font-size: 0.8125rem;
                    font-weight: 400;
                    color: #64748b;
                }

                .setting-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .setting-label {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #374151;
                }










                .sie-summary {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    border-radius: 8px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    margin-bottom: 20px;
                }
                .sie-summary.ok { background: #ecfdf5; border: 1px solid #10b981; color: #065f46; }
                .sie-summary.warn { background: #fffbeb; border: 1px solid #f59e0b; color: #92400e; }
                .sie-summary.error { background: #fef2f2; border: 1px solid #ef4444; color: #991b1b; }
                .sie-summary .sie-summary-counts {
                    margin-left: auto;
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .sie-count-chip {
                    padding: 2px 10px;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    background: rgba(255, 255, 255, 0.7);
                }
                .check-description {
                    font-size: 0.75rem;
                    color: #64748b;
                    margin: 0 0 4px 0;
                }
                .accept-errors-row {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px 16px;
                    margin-top: 12px;
                    background: #fef2f2;
                    border: 1px solid #ef4444;
                    border-radius: 8px;
                    font-size: 0.875rem;
                    color: #991b1b;
                }
                .accept-errors-row input {
                    margin-top: 3px;
                    accent-color: #ef4444;
                    width: 16px;
                    height: 16px;
                    flex-shrink: 0;
                }
                .import-error-box {
                    display: flex;
                    gap: 10px;
                    align-items: flex-start;
                    padding: 12px 16px;
                    margin-bottom: 12px;
                    background: #fef2f2;
                    border: 1px solid #ef4444;
                    border-radius: 8px;
                    font-size: 0.875rem;
                    color: #991b1b;
                }
                .result-panel { text-align: center; padding: 8px 0 4px; }
                .result-done { display: inline-flex; justify-content: center; margin-top: 24px; min-width: 140px; }
                .result-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: var(--rgba-primary-1, #ecfdf5);
                    color: var(--primary, #44814E);
                    margin-bottom: 12px;
                }
            `}</style>

            <div className="sie-modal-content">
                {/* Header */}
                <div className="sie-modal-header">
                    <button className="close-button" onClick={onHide}>
                        <X size={16} />
                    </button>
                    <div className="d-flex align-items-center gap-3">
                        <FileText size={24} color="#64748b" />
                        <div>
                            <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                {importResult ? 'Importen är klar' : 'Kontrollera filen innan import'}
                            </h3>
                            <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                {sourceLine || 'Kontrollera och importera bokföringsdata'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="sie-modal-body">
                    {importResult ? (
                        /* After the import: the confirmation and nothing else.
                           The verify screen already said which year is imported
                           and which become jämförelsetal, what was created is in
                           Kontoplanshälsa, and the momsrapportkod decisions are
                           asked where they belong — in the momsdeklaration
                           (owner, 2026-09-05/06). */
                        <div className="result-panel">
                            <div className="result-icon">
                                <CircleCheckBig size={28} aria-hidden="true" />
                            </div>
                            <h4 style={{ fontWeight: 700, marginBottom: 4 }}>Importen är klar</h4>
                            {vatGapCount > 0 && (
                                <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 12, marginBottom: 0 }}>
                                    <span style={{ display: 'block' }}>
                                        {vatGapCount === 1 ? '1 konto saknar momsrapportkod.' : `${vatGapCount} konton saknar momsrapportkod.`}
                                    </span>
                                    <span style={{ display: 'block' }}>
                                        Det löser du i momsdeklarationen eller under Inställningar → Kontoplan → Hälsa.
                                    </span>
                                </p>
                            )}
                            <button type="button" className="action-button btn-import result-done" onClick={handleCancel}>
                                Klart
                            </button>
                        </div>
                    ) : (
                    <>
                    {/* Check verdict — the first thing the user should see. */}
                    {checks.length > 0 && (
                        <div className={`sie-summary ${hasErrors ? 'error' : warnChecks.length > 0 ? 'warn' : 'ok'}`}>
                            {hasErrors
                                ? <XCircle size={18} />
                                : warnChecks.length > 0 ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                            <span>
                                {hasErrors
                                    ? `Filen innehåller ${errorItemCount || failedChecks.length} fel som du behöver ta ställning till`
                                    : warnChecks.length > 0
                                        ? 'Filen kan importeras — läs varningarna nedan först'
                                        : `Alla ${checks.length} kontroller godkända — filen ser komplett och korrekt ut`}
                            </span>
                            <span className="sie-summary-counts">
                                {failedChecks.length > 0 && <span className="sie-count-chip">{failedChecks.length} fel</span>}
                                {warnChecks.length > 0 && <span className="sie-count-chip">{warnChecks.length} varningar</span>}
                                <span className="sie-count-chip">{passedChecks.length} godkända</span>
                            </span>
                        </div>
                    )}

                    {/* What the file holds — four numbers a reader can use:
                        how much bookkeeping, how many accounts it posts to,
                        which period it covers and whether it carries opening
                        balances. The size of the exported chart and the number
                        of posting rows say nothing about the import. */}
                    <div className="metric-grid">
                        <div className="metric-card">
                            <div className="metric-icon">
                                <FileText size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.verificationCount)}</div>
                            <div className="metric-label">Verifikat</div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <Hash size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.accountsWithTransactions)}</div>
                            <div className="metric-label">Konton som används</div>
                        </div>

                        {(preview.dateRange?.earliest || preview.dateRange?.latest) && (
                            <div className="metric-card">
                                <div className="metric-icon">
                                    <Calendar size={20} />
                                </div>
                                <div className="metric-value metric-value-text">
                                    {preview.dateRange.earliest && preview.dateRange.latest
                                        ? <><span className="nowrap">{preview.dateRange.earliest}</span> – <span className="nowrap">{preview.dateRange.latest}</span></>
                                        : preview.dateRange.earliest || preview.dateRange.latest}
                                </div>
                                <div className="metric-label">Bokförd period</div>
                            </div>
                        )}

                        <div className="metric-card">
                            <div className="metric-icon">
                                <TrendingUp size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.balanceSummary?.openingBalances)}</div>
                            <div className="metric-label">Konton med ingående balans</div>
                        </div>
                    </div>

                    {/* Company — the company only. The file's type, program and
                        export date are file facts and live in the header line. */}
                    <div className="info-section">
                        <div className="section-title">
                            <Building size={18} />
                            Företagsinformation
                        </div>

                        <div className="company-info-grid">
                            <div className="company-card primary">
                                <div className="company-card-icon">
                                    <Building size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">Företagsnamn</div>
                                    <div className="company-card-value">{companyName}</div>
                                </div>
                            </div>

                            <div className="company-card">
                                <div className="company-card-icon">
                                    <Hash size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">Organisationsnummer</div>
                                    <div className="company-card-value">{organizationNumber || 'Ej angivet'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* What the file says about the books, and the settings the
                        user confirms for the year being imported. */}
                    {characteristics && (
                        <div className="characteristics-section">
                            <div className="section-title">
                                <FileSearch size={18} />
                                Tolkning av filen och inställningar
                            </div>
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                                Avläst ur bokföringsmönstren i filen och förifyllt nedan — kontrollera att det stämmer.
                            </p>

                            <div className="characteristics-grid">
                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Bokföringsmetod</h4>
                                        <span
                                            className="confidence-badge"
                                            style={getConfidenceStyle(characteristics.accountingMethod.confidence)}
                                        >
                                            {getConfidenceText(characteristics.accountingMethod.confidence)}
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getMethodLabel(characteristics.accountingMethod.method)}
                                    </div>
                                    {(characteristics.accountingMethod.indicators?.length || 0) > 0 && (
                                        <ul className="characteristic-indicators">
                                            {characteristics.accountingMethod.indicators.slice(0, 3).map((ind, i) => (
                                                <li key={i}>{ind}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Momsperiod</h4>
                                        <span
                                            className="confidence-badge"
                                            style={getConfidenceStyle(characteristics.vatPeriod.confidence)}
                                        >
                                            {getConfidenceText(characteristics.vatPeriod.confidence)}
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getVatPeriodLabel(characteristics.vatPeriod.period)}
                                    </div>
                                    {(characteristics.vatPeriod.indicators?.length || 0) > 0 && (
                                        <ul className="characteristic-indicators">
                                            {characteristics.vatPeriod.indicators.slice(0, 3).map((ind, i) => (
                                                <li key={i}>{ind}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Räkenskapsårets status</h4>
                                        <span
                                            className="confidence-badge"
                                            style={getConfidenceStyle(characteristics.fiscalYearStatus.confidence)}
                                        >
                                            {getConfidenceText(characteristics.fiscalYearStatus.confidence)}
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getStatusLabel(characteristics.fiscalYearStatus.status)}
                                    </div>
                                    {(characteristics.fiscalYearStatus.indicators?.length || 0) > 0 && (
                                        <ul className="characteristic-indicators">
                                            {characteristics.fiscalYearStatus.indicators.slice(0, 3).map((ind, i) => (
                                                <li key={i}>{ind}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {latestFiscalYear && (
                                <div className="fiscal-year-config">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                        <Settings size={16} style={{ color: '#6b7280' }} />
                                        <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                                            Inställningar för räkenskapsår {latestFiscalYear.year}
                                        </h4>
                                        <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                                            {latestFiscalYear.startDate} → {latestFiscalYear.endDate}
                                        </span>
                                    </div>
                                    {!settingsComplete && (
                                        <Alert variant="warning" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem', marginBottom: '1rem' }}>
                                            <AlertTriangle size={14} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                                            Bekräfta alla inställningar nedan innan du importerar.
                                        </Alert>
                                    )}

                                    <div className="settings-grid">
                                        <div className="setting-group">
                                            <label className="setting-label" htmlFor="sie-accounting-method">Bokföringsmetod</label>
                                            <TplSelect
                                                inputId="sie-accounting-method"
                                                options={ACCOUNTING_METHOD_OPTIONS}
                                                value={currentSettings?.accountingMethod || ''}
                                                onChange={(value) => handleFiscalYearSettingChange(latestFiscalYear.year, 'accountingMethod', value)}
                                                placeholder="Välj bokföringsmetod…"
                                            />
                                            {!currentSettings?.accountingMethod && (
                                                <span className="setting-unset">Kunde inte läsas ur filen — välj metod.</span>
                                            )}
                                        </div>

                                        <div className="setting-group">
                                            <label className="setting-label" htmlFor="sie-vat-period">Momsperiod</label>
                                            <TplSelect
                                                inputId="sie-vat-period"
                                                options={VAT_PERIOD_OPTIONS}
                                                value={currentSettings?.vatPeriod || ''}
                                                onChange={(value) => handleFiscalYearSettingChange(latestFiscalYear.year, 'vatPeriod', value)}
                                                placeholder="Välj momsperiod…"
                                            />
                                            {!currentSettings?.vatPeriod && (
                                                <span className="setting-unset">Kunde inte läsas ur filen — välj period.</span>
                                            )}
                                        </div>

                                        {/* Two named states, each with its consequence, instead
                                            of a switch the user has to read the sides of. */}
                                        <div className="setting-group setting-group-wide">
                                            <span className="setting-label" id="sie-fiscal-year-status-label">Räkenskapsårets status</span>
                                            <div className="status-choice" role="radiogroup" aria-labelledby="sie-fiscal-year-status-label">
                                                <label className={`status-card${yearIsOpen ? ' active' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="sie-fiscal-year-status"
                                                        value="open"
                                                        checked={yearIsOpen}
                                                        onChange={() => handleFiscalYearSettingChange(latestFiscalYear.year, 'isOpen', true)}
                                                    />
                                                    <span className="status-card-text">
                                                        <span className="status-card-title">Öppet</span>
                                                        <span className="status-card-desc">Jag fortsätter bokföra i det här året.</span>
                                                    </span>
                                                </label>
                                                <label className={`status-card${yearIsOpen ? '' : ' active'}`}>
                                                    <input
                                                        type="radio"
                                                        name="sie-fiscal-year-status"
                                                        value="closed"
                                                        checked={!yearIsOpen}
                                                        onChange={() => handleFiscalYearSettingChange(latestFiscalYear.year, 'isOpen', false)}
                                                    />
                                                    <span className="status-card-text">
                                                        <span className="status-card-title">Stängt</span>
                                                        <span className="status-card-desc">Bokslutet är klart i det gamla programmet. Året importeras låst, så inga fler verifikat kan bokföras i det.</span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* The years in the file and what each becomes. */}
                    <div className="info-section">
                        <div className="section-title">
                            <Calendar size={18} />
                            Räkenskapsår i filen
                        </div>

                        {preview.fiscalYears && preview.fiscalYears.length > 0 ? (
                            <div className="table-container">
                                <table className="fiscal-years-table">
                                    <thead>
                                        <tr>
                                            <th>Räkenskapsår</th>
                                            <th>Startdatum</th>
                                            <th>Slutdatum</th>
                                            <th>Importeras som</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.fiscalYears.map((year, index) => (
                                            <tr key={index}>
                                                <td className="year-cell">{year.year || `År ${index + 1}`}</td>
                                                <td>{year.startDate || '-'}</td>
                                                <td>{year.endDate || '-'}</td>
                                                <td>
                                                    {index === 0
                                                        ? `Aktuellt räkenskapsår, ${yearIsOpen ? 'öppet' : 'stängt'}`
                                                        : 'Tidigare år, stängt (jämförelsetal)'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data-message">
                                <AlertTriangle size={16} />
                                <span>Filen saknar uppgift om räkenskapsår och kan därför inte importeras. Exportera filen på nytt från ditt gamla program, som en komplett SIE 4-fil.</span>
                            </div>
                        )}
                    </div>

                    {/* Errors and warnings only — the verdict strip at the top
                        says the file is valid when it is. Errors first. */}
                    {(failedChecks.length > 0 || warnChecks.length > 0) && (
                        <FoldedSection
                            id="sie-checks"
                            icon={<AlertTriangle size={18} />}
                            title={failedChecks.length > 0 && warnChecks.length > 0
                                ? 'Fel och varningar'
                                : failedChecks.length > 0 ? 'Fel' : 'Varningar'}
                            detail={failedChecks.length > 0 && warnChecks.length > 0
                                ? `${failedChecks.length} fel, ${warnChecks.length} ${warnChecks.length === 1 ? 'varning' : 'varningar'}`
                                : String(failedChecks.length || warnChecks.length)}
                            defaultOpen
                        >
                            {[...failedChecks, ...warnChecks].map((check) => (
                                <div key={check.id} className={`warning-item ${check.status === 'fail' ? 'warning-error' : ''}`}>
                                    {check.status === 'fail'
                                        ? <XCircle size={16} className="warning-icon error-icon" />
                                        : <AlertTriangle size={16} className="warning-icon" />
                                    }
                                    <div className="warning-content">
                                        <div className="warning-type">{check.name}</div>
                                        {check.description && (
                                            <p className="check-description">{check.description}</p>
                                        )}
                                        {check.items && check.items.length > 0 ? (
                                            check.items.map((item, i) => (
                                                <p key={i} className="warning-message">{item.message}</p>
                                            ))
                                        ) : check.details && (
                                            <p className="warning-message">{check.details}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </FoldedSection>
                    )}
                    </>
                    )}

                    {importError && (
                        <div className="import-error-box">
                            <XCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                            <span>{importError}</span>
                        </div>
                    )}
                </div>

                {/* Actions — none after the import: the panel carries its own
                    centered "Klart". */}
                {!importResult && (
                <div style={{ padding: '0 2rem 1.5rem', background: '#ffffff' }}>
                    {/* The accept-with-errors acknowledgement (owner decision
                        2026-08-25): errors block the import until the user
                        explicitly takes responsibility for them. The backend
                        enforces the same gate — this is not just UI. */}
                    {hasErrors && (
                        <label className="accept-errors-row" htmlFor="sie-accept-errors">
                            <input
                                id="sie-accept-errors"
                                type="checkbox"
                                checked={acceptErrors}
                                onChange={(e) => setAcceptErrors(e.target.checked)}
                                disabled={isImporting}
                            />
                            <span>
                                Jag har läst felen ovan och vill importera filen ändå.
                                Siffror som är fel i filen blir fel även i bokföringen.
                            </span>
                        </label>
                    )}
                    <div className="modal-actions" style={{ padding: '1rem 0 0', margin: 0 }}>
                        <div style={{ fontSize: '0.875rem', color: hasErrors && !acceptErrors ? '#991b1b' : '#64748b' }}>
                            {!settingsComplete
                                ? 'Bekräfta inställningarna för räkenskapsåret innan import'
                                : hasErrors && !acceptErrors
                                    ? 'Filen innehåller fel — bocka i rutan ovan för att kunna importera'
                                    : hasErrors
                                        ? 'Importeras trots fel enligt din bekräftelse'
                                        : 'Filen är redo att importeras'
                            }
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                className="action-button btn-cancel"
                                onClick={handleCancel}
                                disabled={isImporting}
                            >
                                Avbryt
                            </button>
                            <button
                                className="action-button btn-import"
                                onClick={handleConfirm}
                                disabled={!canImport || isImporting}
                            >
                                {isImporting ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        Importerar...
                                    </>
                                ) : (
                                    <>
                                        <Database size={16} />
                                        Importera fil
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                )}
            </div>
        </Modal>
    );
};

export default SieVerificationModal;
